/**
 * Public Token Service
 * Service for managing public token-based operations (no authentication required)
 * Supports both single-step and multi-step forms with progress saving
 * 
 * @see {@link ../types/publicToken.types.js} for type definitions
 */

import { parseUTCDate } from '../utils/dateUtils';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const PUBLIC_TOKEN_BASE = `${API_BASE_URL}/public/v1/token`;

/**
 * Public Token Service
 * All endpoints are public and do not require authentication
 */
class PublicTokenService {
    /**
     * Validate token and get form data (Public)
     * @param {string} token - The secure token ID
     * @returns {Promise<import('../types/publicToken.types').TokenValidationResponse>}
     */
    async validateToken(token) {
        try {
            const response = await fetch(`${PUBLIC_TOKEN_BASE}/${token}`, {
                method: 'GET',
            });

            // Return response as-is (includes valid/error info)
            return await response.json();
        } catch (error) {
            console.error('Failed to validate token:', error);
            return {
                valid: false,
                error: 'network_error',
                message: 'Failed to connect to server. Please check your internet connection.',
            };
        }
    }

    /**
     * Submit form data and consume token (Public - single-step only)
     * For multi-step forms, use getProgress, saveProgress, and completeForm instead
     * @param {string} token - The secure token ID
     * @param {import('../types/publicToken.types').TokenSubmitRequest} data - Form submission data
     * @returns {Promise<import('../types/publicToken.types').TokenSubmitResponse>}
     */
    async submitToken(token, data) {
        try {
            const response = await fetch(`${PUBLIC_TOKEN_BASE}/${token}/submit`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(data),
            });

            // Return response as-is (includes success/error)
            return await response.json();
        } catch (error) {
            console.error('Failed to submit token:', error);
            return {
                success: false,
                error: 'network_error',
                message: 'Failed to submit form. Please check your internet connection.',
            };
        }
    }

    /**
     * Get progress for multi-step form (Public)
     * @param {string} token - The secure token ID
     * @returns {Promise<import('../types/publicToken.types').FormProgressResponse>}
     */
    async getProgress(token) {
        try {
            const response = await fetch(`${PUBLIC_TOKEN_BASE}/${token}/progress`, {
                method: 'GET',
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.error || error.message || 'Failed to get progress');
            }

            return await response.json();
        } catch (error) {
            console.error('Failed to get progress:', error);
            throw error;
        }
    }

    /**
     * Save progress for a step (Public)
     * @param {string} token - The secure token ID
     * @param {import('../types/publicToken.types').SaveProgressRequest} request - Save progress request
     * @returns {Promise<import('../types/publicToken.types').SaveProgressResponse>}
     */
    async saveProgress(token, request) {
        try {
            const response = await fetch(`${PUBLIC_TOKEN_BASE}/${token}/save`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(request),
            });

            return await response.json();
        } catch (error) {
            console.error('Failed to save progress:', error);
            return {
                success: false,
                error: 'network_error',
                message: 'Failed to save progress. Please try again.',
                currentStep: request.stepNumber || 1,
                completedSteps: [],
            };
        }
    }

    /**
     * Complete multi-step form (Public)
     * @param {string} token - The secure token ID
     * @param {import('../types/publicToken.types').CompleteFormRequest} request - Complete form request
     * @returns {Promise<import('../types/publicToken.types').CompleteFormResponse>}
     */
    async completeForm(token, request) {
        try {
            const response = await fetch(`${PUBLIC_TOKEN_BASE}/${token}/complete`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(request),
            });

            return await response.json();
        } catch (error) {
            console.error('Failed to complete form:', error);
            return {
                success: false,
                error: 'network_error',
                message: 'Failed to complete form. Please try again.',
                completedAt: null,
                allStepData: {},
            };
        }
    }

    /**
     * Helper: Check if token supports multi-step workflow
     * @param {import('../types/publicToken.types').TokenValidationResponse} tokenData
     * @returns {boolean}
     */
    isMultiStepForm(tokenData) {
        return !!(
            tokenData.valid &&
            tokenData.allowMultipleSubmissions &&
            tokenData.totalSteps &&
            tokenData.totalSteps > 1
        );
    }

    /**
     * Helper: Get expiration time remaining
     * @param {string} expiresAt - UTC datetime string
     * @returns {{hours: number, minutes: number, expired: boolean}}
     */
    getTimeRemaining(expiresAt) {
        if (!expiresAt) {
            return { hours: 0, minutes: 0, expired: true };
        }

        const now = new Date();
        const expiry = parseUTCDate(expiresAt);
        const diffMs = expiry - now;

        if (diffMs <= 0) {
            return { hours: 0, minutes: 0, expired: true };
        }

        const hours = Math.floor(diffMs / (1000 * 60 * 60));
        const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

        return { hours, minutes, expired: false };
    }

    /**
     * Helper: Format expiration time for display
     * @param {string} expiresAt - ISO 8601 datetime
     * @returns {string}
     */
    formatExpirationTime(expiresAt) {
        if (!expiresAt) return 'Unknown';

        const { hours, minutes, expired } = this.getTimeRemaining(expiresAt);

        if (expired) {
            return 'Expired';
        }

        if (hours > 24) {
            const days = Math.floor(hours / 24);
            return `${days} day${days !== 1 ? 's' : ''}`;
        }

        if (hours > 0) {
            return `${hours} hour${hours !== 1 ? 's' : ''} ${minutes} min`;
        }

        return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
    }

    /**
     * Helper: Get purpose-specific title
     * @param {string} purpose - Token purpose
     * @returns {string}
     */
    getPurposeTitle(purpose) {
        const titles = {
            client_onboarding: 'Complete Your Registration',
            password_reset: 'Reset Your Password',
            email_verification: 'Verify Your Email',
            document_share: 'Access Shared Document',
            contact_invitation: 'Join Our Platform',
        };
        return titles[purpose] || 'Complete Form';
    }
}

// Export singleton instance
const publicTokenService = new PublicTokenService();
export default publicTokenService;
